// Billing panels (#168 WS2 — BILLING_TIERS.md §8/§9 UI):
//   OrgPlanTab        — OrgSettings → Plan: subscription + trial banner, usage
//                       bars from /entitlements percentages, plan comparison
//                       cards with checkout CTA, org usage dashboard.
//   UserUsageTab      — UserSettings → Usage: the caller's personal usage rows.
//   PlatformBillingTab— PlatformConsole → Billing: orgs × plan × usage overview,
//                       manual plan grant, org/user override editor, usage reset.
// All data comes from billingApi; every mutating action is backend-gated
// (org admin / super admin) — the UI only hides what the caller can't use.
import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, CardHeader, Chip, CircularProgress, LinearProgress,
  MenuItem, Select, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField,
  Typography,
} from '@mui/material';
import billingApi from '../services/billingApi';
import iamApi from '../services/iamApi';
import ConfirmDialog from '../components/dialogs/ConfirmDialog';
import { useAuth } from '../contexts/AuthContext';

/**
 * Fetch-on-deps hook shared by the three panels in this file.
 *
 * #968 Phase A: the return was inferred from the initial state alone, so it came
 * out as `{ reload: () => void; loading: boolean }`. Every `.data` / `.error` read
 * below was then an error — 23 of them, one per call site, from this one
 * un-annotated function. `data` is generic over what `fn` resolves to, so a call
 * site keeps whatever billingApi/iamApi actually promise. An `any` here would have
 * moved the same 23 without checking any of them, and the generic sharpens on its
 * own once #967's generated contract reaches these services.
 *
 * @template T
 * @param {() => Promise<T>} fn Resolves to the panel's payload. A fresh arrow per
 *   render, which is why it is absent from `deps` (see the note below).
 * @param {React.DependencyList} deps
 * @returns {{ data?: T, error?: Error, loading: boolean, reload: () => void }}
 */
function useAsync(fn, deps) {
  const [state, setState] = useState(
    /** @type {{ data?: T, error?: Error, loading: boolean }} */ ({ loading: true }),
  );
  const reload = () => {
    setState((s) => ({ ...s, loading: true }));
    fn().then((data) => setState({ data, loading: false }))
      .catch((error) => setState({ error, loading: false }));
  };
  // Unverifiable by construction rather than a deferred fix: `deps` is a VARIABLE handed
  // in by the caller, and the rule can only check an array literal written at the hook
  // call itself. `fn` is excluded for the same reason it is in `iam/pages.jsx`'s useAsync
  // — every caller passes a fresh arrow, so depending on it would refetch every render.
  // Each `useAsync(...)` call site below owns its own invalidation list.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(reload, deps);
  return { ...state, reload };
}

const KIND_LABELS = {
  ai_calls: 'AI calls', ai_tokens: 'AI tokens', api_calls: 'API calls',
  usage_warning: 'Limit warnings',
};
const ENT_LABELS = {
  max_projects: 'Projects', max_requirements: 'Requirements', max_seats: 'Seats',
  max_mcp_tokens: 'MCP tokens', max_custom_item_types: 'Custom item types',
  ai_calls_per_month: 'AI calls / month', ai_tokens_per_month: 'AI tokens / month',
  api_calls_per_month: 'API calls / month',
  ai_tokens_per_user_per_month: 'AI tokens / user / month',
  shared_projects: 'Multi-user projects',
  premium_models: 'Premium AI models', mcp_access: 'Claude / MCP access',
  external_api_access: 'External API (v1)', document_import: 'Document import',
  advanced_traceability: 'Advanced traceability', engineering_toolkit: 'Engineering toolkit',
  dashboards: 'Dashboards', history_retention_days: 'History retention (days)',
};
const label = (k) => ENT_LABELS[k] || KIND_LABELS[k] || k;
const fmtLimit = (v) => (v === null || v === undefined ? 'Unlimited' : v.toLocaleString());
const fmtPrice = (pence, currency = 'GBP') => {
  if (pence === null || pence === undefined) return 'Contact us';
  const sym = currency === 'GBP' ? '£' : currency === 'USD' ? '$' : `${currency} `;
  return `${sym}${(pence / 100).toFixed(pence % 100 ? 2 : 0)}`;
};
// #564: renewal/period line. `current_period_end` was a dead column until the Paddle
// webhook started writing it, so this renders nothing at all for a subscription that
// has never seen a provider event (manual/admin grants, and every org today) — absence
// is the truth there, and inventing a date would be worse than showing none.
const fmtDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString();
};

const trialDaysLeft = (iso) => {
  if (!iso) return null;
  const d = Math.ceil((new Date(iso) - Date.now()) / 86400000);
  return d > 0 ? d : 0;
};

function UsageBar({ ent }) {
  const pct = ent.percent ?? 0;
  return (
    <Box sx={{ mb: 1.5 }} data-testid={`usage-bar-${ent.key}`}>
      <Stack direction="row" sx={{ mb: 0.25, justifyContent: 'space-between' }}>
        <Typography variant="body2">{label(ent.key)}</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {(ent.usage ?? 0).toLocaleString()} / {fmtLimit(ent.limit)}
        </Typography>
      </Stack>
      <LinearProgress variant="determinate" value={ent.limit ? pct : 0}
        color={pct >= 100 ? 'error' : pct >= 80 ? 'warning' : 'primary'}
        sx={{ height: 8, borderRadius: 1 }} />
    </Box>
  );
}

// Usage rows come back with '' sentinels: ('','') = org aggregate,
// (uid,'') = per-member, ('',pid) = per-project.
const orgRows = (rows) => (rows || []).filter((r) => r.user_id === '' && r.project_id === '');
const memberRows = (rows) => (rows || []).filter((r) => r.user_id !== '' && r.project_id === '');
const projectRows = (rows) => (rows || []).filter((r) => r.project_id !== '' && r.user_id === '');

// `resolve` maps the raw id in `byCol` (a user_id / project_id GUID) to a friendly
// name (email / project title); falls back to the id when it can't be resolved.
function UsageTable({ rows, byCol, byLabel, testid, resolve }) {
  if (!rows?.length) return <Typography variant="body2" sx={{ color: 'text.secondary' }}>No usage recorded this period.</Typography>;
  return (
    <Box className="scroll-x"><Table size="small" data-testid={testid}>
      <TableHead><TableRow>
        {byCol && <TableCell>{byLabel}</TableCell>}
        <TableCell>Meter</TableCell><TableCell align="right">Quantity</TableCell>
      </TableRow></TableHead>
      <TableBody>{rows.map((r) => (
        <TableRow key={`${byCol ? r[byCol] : 'org'}-${r.kind}`}>
          {byCol && <TableCell>{resolve ? resolve(r[byCol]) : r[byCol]}</TableCell>}
          <TableCell>{label(r.kind)}</TableCell>
          <TableCell align="right">{r.quantity.toLocaleString()}</TableCell>
        </TableRow>
      ))}</TableBody>
    </Table></Box>
  );
}

// ---------------------------------------------------------------- OrgPlanTab
export function OrgPlanTab() {
  const { activeOrg } = useAuth() || {};
  const ents = useAsync(() => billingApi.entitlements(), []);
  const plans = useAsync(() => billingApi.plans(), []);
  const usage = useAsync(() => billingApi.usage('org').catch(() => null), []); // member-role callers get no org scope
  // Resolve the usage tables' user_id / project_id GUIDs to friendly names (#414-adjacent).
  const members = useAsync(() => (activeOrg ? iamApi.orgMembers(activeOrg).catch(() => []) : Promise.resolve([])), [activeOrg]);
  const projects = useAsync(() => (activeOrg ? iamApi.myProjects(activeOrg).catch(() => []) : Promise.resolve([])), [activeOrg]);
  const memberName = (uid) => {
    const m = (members.data || []).find((x) => x.user_id === uid);
    return m?.email || m?.display_name || uid;
  };
  const projectName = (pid) => {
    const p = (projects.data || []).find((x) => x.id === pid);
    return p?.name || p?.title || pid;
  };
  const [msg, setMsg] = useState(null);
  const [busyPlan, setBusyPlan] = useState(null);

  const upgrade = (planKey) => {
    setMsg(null); setBusyPlan(planKey);
    billingApi.checkout(planKey)
      .then((r) => {
        if (r.url) window.open(r.url, '_blank', 'noopener');
        else setMsg({ severity: r.available === false ? 'info' : 'success', text: r.message || 'Checkout created.' });
      })
      .catch((e) => setMsg({ severity: 'error', text: e?.response?.data?.detail || 'Checkout failed.' }))
      .finally(() => setBusyPlan(null));
  };

  const d = ents.data;
  const days = d?.status === 'trialing' ? trialDaysLeft(d.trial_ends_at) : null;
  const limits = (d?.entitlements || []).filter((e) => e.kind === 'limit' && !e.reserved && e.usage !== null && e.usage !== undefined);
  const flags = (d?.entitlements || []).filter((e) => e.kind === 'flag' && !e.reserved);

  return (
    <Box data-testid="org-plan-tab">
      {msg && <Alert sx={{ mb: 2 }} severity={msg.severity} onClose={() => setMsg(null)}>{msg.text}</Alert>}
      {ents.error && <Alert severity="error" sx={{ mb: 2 }}>Could not load the plan for this organization.</Alert>}
      {/* #818: without this the tab just rendered blank (no error, no plan) for
          however long the fetch took — indistinguishable from "nothing to show". */}
      {ents.loading && !d && (
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', color: 'text.secondary', mb: 2 }}>
          <CircularProgress size={14} thickness={5} />
          <Typography variant="body2">Loading plan…</Typography>
        </Stack>
      )}
      {d && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography variant="h6" data-testid="current-plan">Current plan: {d.plan}</Typography>
              <Chip size="small" label={d.status} color={d.status === 'past_due' ? 'warning' : 'default'} />
            </Stack>
            {days !== null && (
              <Alert severity={days <= 3 ? 'warning' : 'info'} sx={{ mt: 1 }} data-testid="trial-banner">
                Trial: {days} day{days === 1 ? '' : 's'} left — pick a plan below to keep these features.
              </Alert>
            )}
            {fmtDate(d.current_period_end) && (
              <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}
                data-testid="renewal-line">
                {d.cancel_at_period_end
                  ? `Cancels on ${fmtDate(d.current_period_end)} — access continues until then.`
                  : `Renews on ${fmtDate(d.current_period_end)}.`}
              </Typography>
            )}
            <Box sx={{ mt: 2, maxWidth: 560 }}>
              {limits.map((e) => <UsageBar key={e.key} ent={e} />)}
            </Box>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mt: 1 }} useFlexGap>
              {flags.map((e) => (
                <Chip key={e.key} size="small" variant="outlined" label={label(e.key)}
                  color={e.flag ? 'success' : 'default'} disabled={!e.flag} />
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      <Typography variant="subtitle1" sx={{ mb: 1 }}>Plans</Typography>
      <Stack direction="row" spacing={2} useFlexGap sx={{ flexWrap: 'wrap', mb: 2 }} data-testid="plan-cards">
        {(plans.data || []).map((p) => {
          const current = d && p.key === d.plan;
          return (
            <Card key={p.key} variant="outlined"
              sx={{ width: 220, ...(current ? { borderColor: 'primary.main', borderWidth: 2 } : {}) }}>
              <CardContent>
                <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6">{p.name}</Typography>
                  {current && <Chip size="small" color="primary" label="Current" />}
                </Stack>
                <Typography variant="h5" sx={{ my: 1 }}>
                  {fmtPrice(p.price_monthly_pence, p.currency)}
                  {p.price_monthly_pence !== null && p.price_monthly_pence !== undefined
                    && <Typography component="span" variant="body2" sx={{ color: 'text.secondary' }}>/mo</Typography>}
                </Typography>
                {p.description && <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>{p.description}</Typography>}
                <Typography variant="caption" component="div" sx={{ mb: 1, color: 'text.secondary' }}>
                  {['max_projects', 'max_requirements', 'max_seats'].map((k) => (
                    k in (p.entitlements || {})
                      ? `${fmtLimit(p.entitlements[k])} ${label(k).toLowerCase()}` : null
                  )).filter(Boolean).join(' · ')}
                </Typography>
                {!current && (
                  <Button size="small" variant="contained" fullWidth disabled={busyPlan === p.key}
                    data-testid={`upgrade-${p.key}`} onClick={() => upgrade(p.key)}>
                    {busyPlan === p.key ? 'Opening…' : 'Choose plan'}
                  </Button>
                )}
              </CardContent>
            </Card>
          );
        })}
      </Stack>

      {usage.data && (
        <>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Organization usage — {usage.data.period}
          </Typography>
          <Stack spacing={2}>
            <UsageTable rows={orgRows(usage.data.rows)} testid="org-usage-total" />
            {memberRows(usage.data.rows).length > 0 && (
              <UsageTable rows={memberRows(usage.data.rows)} byCol="user_id" byLabel="Member" testid="org-usage-members" resolve={memberName} />
            )}
            {projectRows(usage.data.rows).length > 0 && (
              <UsageTable rows={projectRows(usage.data.rows)} byCol="project_id" byLabel="Project" testid="org-usage-projects" resolve={projectName} />
            )}
          </Stack>
        </>
      )}
    </Box>
  );
}

// --------------------------------------------------------------- UserUsageTab
export function UserUsageTab() {
  const usage = useAsync(() => billingApi.usage('me'), []);
  return (
    <Box data-testid="user-usage-tab">
      {usage.error && <Alert severity="error" sx={{ mb: 2 }}>Could not load your usage.</Alert>}
      {usage.data && (
        <>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            Your personal usage in the active organization for {usage.data.period}. Org-level
            totals live in the organization&apos;s Plan tab.
          </Typography>
          <UsageTable rows={(usage.data.rows || []).filter((r) => r.project_id === '')} testid="my-usage" />
        </>
      )}
    </Box>
  );
}

// --------------------------------------------------------- PlatformBillingTab
const OVERRIDE_KEYS = Object.keys(ENT_LABELS);
// Mirrors the backend registry's kind= for the override editor's value coercion
// (a wrong guess is caught server-side by the key/kind validation anyway).
const FLAG_KEYS = new Set(['premium_models', 'mcp_access', 'external_api_access',
  'document_import', 'advanced_traceability', 'engineering_toolkit', 'dashboards',
  'shared_projects']);

// #563: a blank price box means "no price set" (NULL), which is NOT the same as free
// (0). Coercing blank to 0 would silently price a plan at zero — the kind of edit
// nobody re-reads because it looks like it did nothing.
//
// Price inputs hold the TYPED TEXT, converted only on save. Deriving the input's value
// from the stored pence instead makes the field uneditable in a way that is easy to
// miss and impossible to work around: after typing "12." the round trip is
// Number("12.") === 12 -> 1200 pence -> "12", so the decimal point vanishes as it is
// typed and no price with pence can ever be entered.
const penceFromPounds = (v) => {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? Math.round(n * 100) : null;
};
const poundsFromPence = (v) => (v === null || v === undefined ? '' : String(v / 100));
const ENFORCEMENTS = ['hard', 'warn', 'off'];
const PLAN_DRAFT = {
  key: '', name: '', description: '', monthlyText: '', yearlyText: '',
  is_public: true, display_order: 0,
};

// #1033: PlatformBillingTab's four blocks used to render straight onto the
// PlatformConsole page background (bare Typography + Table, no surface) — every
// OTHER tab in that console wraps its content in a `Section` (iam/pages.jsx: a
// Card + CardHeader + CardContent, giving `background.paper`), but `Section`
// is a private, unexported function there and this file must not edit
// pages.jsx (owned by a different area). `Panel` reproduces the same visual
// result locally: a white (light) / raised (dark) card surface via MUI's
// themed `Card`, matching every other panel in the app instead of leaving the
// grey page background showing through the table rows. Table overflow itself
// already scrolls via `.scroll-x` (styles/screens.css) rather than clipping —
// wrapping the table in a card doesn't change that, it just gives the
// scrollable area a proper opaque surface to scroll ON.
//
// `sx` is optional — three of the four call sites below don't need an extra
// override — so it needs a default. A literal `= {}` default would build a new
// object every render (react/no-object-type-as-default-prop, #943); reuse one
// frozen singleton instead (#968: without either, the inferred prop type made
// `sx` required and every sx-less call site failed checkJs).
const EMPTY_SX = Object.freeze({});

/**
 * @param {{ title: string, children: React.ReactNode, sx?: object }} props
 */
function Panel({ title, children, sx = EMPTY_SX }) {
  return (
    <Card sx={{ mb: 3, ...sx }}>
      <CardHeader title={title} slotProps={{ title: { variant: 'subtitle1' } }} />
      <CardContent sx={{ pt: 0 }}>{children}</CardContent>
    </Card>
  );
}

export function PlatformBillingTab() {
  const overview = useAsync(() => billingApi.adminOverview(), []);
  const plans = useAsync(() => billingApi.plans(), []);
  // The ADMIN listing, not the public catalog — see billingApi.adminPlans for why
  // editing on top of `plans()` would strand any plan whose Public box is unticked.
  const adminPlans = useAsync(() => billingApi.adminPlans(), []);
  const [msg, setMsg] = useState(null);
  const [grant, setGrant] = useState({});           // org_id -> plan key pending grant
  const [ov, setOv] = useState({ orgId: '', userId: '', key: OVERRIDE_KEYS[0], value: '', expires: '' });
  const [planDraft, setPlanDraft] = useState({});   // plan_id -> partial edits
  const [newPlan, setNewPlan] = useState(PLAN_DRAFT);
  const [pe, setPe] = useState({ planId: '', key: OVERRIDE_KEYS[0], value: '', enforcement: 'hard' });
  // In-app confirmation modal (replaces window.confirm, #943). Holds the descriptor
  // for the pending action — { title, message, severity, confirmLabel, confirmColor,
  // testId, onConfirm } — or null when nothing is awaiting confirmation.
  const [confirm, setConfirm] = useState(null);

  const act = (label_, fn, reload = true) => {
    setMsg(null);
    fn().then(() => { setMsg({ severity: 'success', text: `${label_} done.` }); if (reload) overview.reload(); })
      .catch((e) => setMsg({ severity: 'error', text: e?.response?.data?.detail || `${label_} failed.` }));
  };

  const applyOverride = (del = false) => {
    const { orgId, userId, key, value, expires } = ov;
    if (!orgId) return;
    const isFlag = FLAG_KEYS.has(key);
    const body = {
      ...(isFlag ? { flag_value: value === 'true' } : { limit_value: value === '' ? null : Number(value) }),
      ...(expires ? { expires_at: expires } : {}),
    };
    if (del) {
      act('Override removal', () => (userId
        ? billingApi.adminDelUserOverride(orgId, userId, key)
        : billingApi.adminDelOverride(orgId, key)), false);
    } else {
      act('Override', () => (userId
        ? billingApi.adminSetUserOverride(orgId, userId, key, body)
        : billingApi.adminSetOverride(orgId, key, body)), false);
    }
  };

  // ---- plan editing (#563) -------------------------------------------------
  // A row's edit state is the saved plan overlaid with whatever has been typed, so an
  // untouched field round-trips its stored value rather than being sent as blank.
  const planRow = (p) => ({ ...p, ...(planDraft[p.id] || {}) });
  const setPlanField = (pid, field, value) =>
    setPlanDraft((d) => ({ ...d, [pid]: { ...(d[pid] || {}), [field]: value } }));

  // A price field that was never touched has no draft text, so it round-trips its
  // STORED pence rather than being re-derived from a rendering of itself.
  const priceOf = (p, textField, storedField) => (p[textField] === undefined
    ? (p[storedField] ?? null)
    : penceFromPounds(p[textField]));

  const planBody = (p) => ({
    key: p.key,
    name: p.name,
    description: p.description || null,
    price_monthly_pence: priceOf(p, 'monthlyText', 'price_monthly_pence'),
    price_yearly_pence: priceOf(p, 'yearlyText', 'price_yearly_pence'),
    is_public: !!p.is_public,
    display_order: Number(p.display_order) || 0,
  });

  const reloadPlans = () => { adminPlans.reload(); plans.reload(); };

  const savePlan = (p) => act(`Plan ${p.key}`,
    () => billingApi.adminUpdatePlan(p.id, planBody(p)).then(() => {
      setPlanDraft((d) => { const n = { ...d }; delete n[p.id]; return n; });
      reloadPlans();
    }), false);

  const createPlan = () => act(`Plan ${newPlan.key} created`,
    () => billingApi.adminCreatePlan(planBody(newPlan)).then(() => {
      setNewPlan(PLAN_DRAFT);
      reloadPlans();
    }), false);

  const savePlanEntitlement = () => {
    const isFlag = FLAG_KEYS.has(pe.key);
    const body = {
      ...(isFlag
        ? { flag_value: pe.value === 'true' }
        : { limit_value: pe.value === '' ? null : Number(pe.value) }),
      enforcement: pe.enforcement,
    };
    act(`Entitlement ${label(pe.key)}`,
      () => billingApi.adminSetPlanEntitlement(pe.planId, pe.key, body).then(reloadPlans),
      false);
  };

  // Raw rows from the admin listing keep `enforcement`, which the public catalog drops.
  const entValue = (p, key) => {
    const row = (p.entitlements || []).find((e) => e.key === key);
    if (!row) return '—';
    return FLAG_KEYS.has(key) ? String(!!row.flag_value)
      : (row.limit_value === null || row.limit_value === undefined ? 'unlimited' : row.limit_value);
  };

  const usageCell = (u) => ['ai_calls', 'ai_tokens', 'api_calls']
    .map((k) => (u?.[k] ? `${label(k)}: ${u[k].toLocaleString()}` : null))
    .filter(Boolean).join(' · ') || '—';

  return (
    <Box data-testid="platform-billing-tab">
      {msg && <Alert sx={{ mb: 2 }} severity={msg.severity} onClose={() => setMsg(null)}>{msg.text}</Alert>}

      <Panel title={`Organizations — ${overview.data?.period || 'current period'}`}>
      <Box className="scroll-x"><Table size="small" data-testid="billing-overview">
        <TableHead><TableRow>
          <TableCell>Organization</TableCell><TableCell>Plan</TableCell><TableCell>Status</TableCell>
          <TableCell>Usage (this period)</TableCell><TableCell align="right">Actions</TableCell>
        </TableRow></TableHead>
        <TableBody>{(overview.data?.orgs || []).map((o) => (
          <TableRow key={o.id}>
            <TableCell>{o.name}</TableCell>
            <TableCell>{o.plan_key || o.plan || 'free'}{o.status === 'trialing' ? ' (trial)' : ''}</TableCell>
            <TableCell>{o.status || 'active'}</TableCell>
            <TableCell>{usageCell(o.usage)}</TableCell>
            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
              <Select size="small" value={grant[o.id] || o.plan_key || 'free'} sx={{ mr: 1, minWidth: 110 }}
                onChange={(e) => setGrant((g) => ({ ...g, [o.id]: e.target.value }))}
                data-testid={`grant-select-${o.id}`}>
                {(plans.data || []).map((p) => <MenuItem key={p.key} value={p.key}>{p.name}</MenuItem>)}
              </Select>
              <Button disabled={!grant[o.id] || grant[o.id] === o.plan_key}
                onClick={() => act(`Plan grant (${grant[o.id]})`, () => billingApi.adminSetSubscription(o.id, grant[o.id]))}>
                Grant
              </Button>
              <Button color="warning" onClick={() => setConfirm({
                title: 'Reset usage rollups',
                message: `Reset ${o.name}'s usage rollups for this period? Events are kept and can be recomputed.`,
                confirmLabel: 'Reset usage',
                testId: 'reset-usage-confirm',
                onConfirm: () => act('Usage reset', () => billingApi.adminResetUsage(o.id, {})),
              })} data-testid="reset-usage-btn">Reset usage</Button>
            </TableCell>
          </TableRow>
        ))}</TableBody>
      </Table></Box>
      </Panel>

      <Panel title="Entitlement override">
      <Typography variant="body2" sx={{ mb: 1, maxWidth: 720, color: 'text.secondary' }}>
        Precedence: user override &gt; org override &gt; plan &gt; default. Leave the user blank
        for an org-wide override; leave the value blank to set a limit to unlimited. Flags take
        true/false. An expiry makes it temporary (ISO date-time).
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap sx={{ flexWrap: 'wrap', alignItems: { xs: 'stretch', sm: 'flex-end' } }}>
        <Select size="small" displayEmpty value={ov.orgId} sx={{ minWidth: 160 }} data-testid="ov-org"
          onChange={(e) => setOv((s) => ({ ...s, orgId: e.target.value }))}>
          <MenuItem value="" disabled>Organization…</MenuItem>
          {(overview.data?.orgs || []).map((o) => <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>)}
        </Select>
        <TextField size="small" label="User id (optional)" value={ov.userId} sx={{ minWidth: 160 }}
          onChange={(e) => setOv((s) => ({ ...s, userId: e.target.value.trim() }))} />
        <Select size="small" value={ov.key} sx={{ minWidth: 200 }} data-testid="ov-key"
          onChange={(e) => setOv((s) => ({ ...s, key: e.target.value }))}>
          {OVERRIDE_KEYS.map((k) => <MenuItem key={k} value={k}>{label(k)}</MenuItem>)}
        </Select>
        <TextField size="small" label="Value" value={ov.value} sx={{ width: 120 }}
          data-testid="ov-value"
          onChange={(e) => setOv((s) => ({ ...s, value: e.target.value.trim() }))} />
        <TextField size="small" label="Expires (optional)" value={ov.expires} sx={{ minWidth: 180 }}
          placeholder="2026-12-31T00:00:00Z"
          onChange={(e) => setOv((s) => ({ ...s, expires: e.target.value.trim() }))} />
        <Button variant="contained" disabled={!ov.orgId} data-testid="ov-apply"
          onClick={() => applyOverride(false)}>Apply</Button>
        <Button color="error" disabled={!ov.orgId} data-testid="ov-remove"
          onClick={() => applyOverride(true)}>Remove</Button>
      </Stack>
      </Panel>

      {/* ---- Plans (#563): the ladder is data, so it must be editable in-product --- */}
      <Panel title="Plans">
      <Typography variant="body2" sx={{ mb: 1, maxWidth: 720, color: 'text.secondary' }}>
        Prices are in pounds; leave a price blank for &ldquo;no price set&rdquo; (which is not
        the same as free). Unticking Public hides a plan from the pricing page — it stays
        listed and editable here. Order controls the pricing-page sequence.
      </Typography>
      <Box className="scroll-x"><Table size="small" data-testid="plans-editor">
        <TableHead><TableRow>
          <TableCell>Key</TableCell><TableCell>Name</TableCell>
          <TableCell>Monthly £</TableCell><TableCell>Yearly £</TableCell>
          <TableCell>Order</TableCell><TableCell>Public</TableCell>
          <TableCell align="right">Actions</TableCell>
        </TableRow></TableHead>
        <TableBody>
          {(adminPlans.data || []).map((saved) => {
            const p = planRow(saved);
            const dirty = !!planDraft[saved.id];
            return (
              <TableRow key={saved.id} data-testid={`plan-row-${saved.key}`}>
                <TableCell>{p.key}</TableCell>
                <TableCell>
                  <TextField size="small" value={p.name || ''} data-testid={`plan-name-${saved.key}`}
                    onChange={(e) => setPlanField(saved.id, 'name', e.target.value)} />
                </TableCell>
                <TableCell>
                  <TextField size="small" sx={{ width: 100 }}
                    value={p.monthlyText !== undefined ? p.monthlyText : poundsFromPence(p.price_monthly_pence)}
                    data-testid={`plan-monthly-${saved.key}`}
                    onChange={(e) => setPlanField(saved.id, 'monthlyText', e.target.value)} />
                </TableCell>
                <TableCell>
                  <TextField size="small" sx={{ width: 100 }}
                    value={p.yearlyText !== undefined ? p.yearlyText : poundsFromPence(p.price_yearly_pence)}
                    data-testid={`plan-yearly-${saved.key}`}
                    onChange={(e) => setPlanField(saved.id, 'yearlyText', e.target.value)} />
                </TableCell>
                <TableCell>
                  <TextField size="small" sx={{ width: 70 }} value={String(p.display_order ?? 0)}
                    data-testid={`plan-order-${saved.key}`}
                    onChange={(e) => setPlanField(saved.id, 'display_order', e.target.value.trim())} />
                </TableCell>
                <TableCell>
                  <Select size="small" value={p.is_public ? 'true' : 'false'} sx={{ minWidth: 90 }}
                    data-testid={`plan-public-${saved.key}`}
                    onChange={(e) => setPlanField(saved.id, 'is_public', e.target.value === 'true')}>
                    <MenuItem value="true">Yes</MenuItem>
                    <MenuItem value="false">No</MenuItem>
                  </Select>
                </TableCell>
                <TableCell align="right">
                  <Button variant="contained" size="small" disabled={!dirty}
                    data-testid={`plan-save-${saved.key}`} onClick={() => savePlan(p)}>Save</Button>
                </TableCell>
              </TableRow>
            );
          })}
          <TableRow data-testid="plan-row-new">
            <TableCell>
              <TextField size="small" placeholder="key" value={newPlan.key} data-testid="plan-new-key"
                onChange={(e) => setNewPlan((s) => ({ ...s, key: e.target.value.trim() }))} />
            </TableCell>
            <TableCell>
              <TextField size="small" placeholder="Name" value={newPlan.name} data-testid="plan-new-name"
                onChange={(e) => setNewPlan((s) => ({ ...s, name: e.target.value }))} />
            </TableCell>
            <TableCell>
              <TextField size="small" sx={{ width: 100 }} value={newPlan.monthlyText}
                data-testid="plan-new-monthly"
                onChange={(e) => setNewPlan((s) => ({ ...s, monthlyText: e.target.value }))} />
            </TableCell>
            <TableCell>
              <TextField size="small" sx={{ width: 100 }} value={newPlan.yearlyText}
                data-testid="plan-new-yearly"
                onChange={(e) => setNewPlan((s) => ({ ...s, yearlyText: e.target.value }))} />
            </TableCell>
            <TableCell>
              <TextField size="small" sx={{ width: 70 }} value={String(newPlan.display_order)}
                data-testid="plan-new-order"
                onChange={(e) => setNewPlan((s) => ({ ...s, display_order: e.target.value.trim() }))} />
            </TableCell>
            <TableCell>
              <Select size="small" value={newPlan.is_public ? 'true' : 'false'} sx={{ minWidth: 90 }}
                data-testid="plan-new-public"
                onChange={(e) => setNewPlan((s) => ({ ...s, is_public: e.target.value === 'true' }))}>
                <MenuItem value="true">Yes</MenuItem>
                <MenuItem value="false">No</MenuItem>
              </Select>
            </TableCell>
            <TableCell align="right">
              <Button variant="contained" size="small" disabled={!newPlan.key || !newPlan.name}
                data-testid="plan-create" onClick={createPlan}>Create</Button>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table></Box>
      </Panel>

      <Panel title="Plan entitlements" sx={{ mb: 2 }}>
      <Typography variant="body2" sx={{ mb: 1, maxWidth: 720, color: 'text.secondary' }}>
        Sets what a plan grants, for every org on it. Limits take a number (blank =
        unlimited); flags take true/false. Enforcement: hard blocks, warn allows and
        reports, off disables the check.
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap sx={{ flexWrap: 'wrap', mb: 1, alignItems: { xs: 'stretch', sm: 'flex-end' } }}>
        <Select size="small" displayEmpty value={pe.planId} sx={{ minWidth: 160 }} data-testid="pe-plan"
          onChange={(e) => setPe((s) => ({ ...s, planId: e.target.value }))}>
          <MenuItem value="" disabled>Plan…</MenuItem>
          {(adminPlans.data || []).map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
        </Select>
        <Select size="small" value={pe.key} sx={{ minWidth: 200 }} data-testid="pe-key"
          onChange={(e) => setPe((s) => ({ ...s, key: e.target.value }))}>
          {OVERRIDE_KEYS.map((k) => <MenuItem key={k} value={k}>{label(k)}</MenuItem>)}
        </Select>
        <TextField size="small" label="Entitlement value" value={pe.value} sx={{ width: 150 }}
          data-testid="pe-value"
          onChange={(e) => setPe((s) => ({ ...s, value: e.target.value.trim() }))} />
        <Select size="small" value={pe.enforcement} sx={{ minWidth: 120 }} data-testid="pe-enforcement"
          onChange={(e) => setPe((s) => ({ ...s, enforcement: e.target.value }))}>
          {ENFORCEMENTS.map((x) => <MenuItem key={x} value={x}>{x}</MenuItem>)}
        </Select>
        <Button variant="contained" disabled={!pe.planId} data-testid="pe-apply"
          onClick={savePlanEntitlement}>Set</Button>
      </Stack>
      {pe.planId && (
        <Typography variant="body2" data-testid="pe-current" sx={{ color: 'text.secondary' }}>
          Current: {label(pe.key)} = {entValue(
            (adminPlans.data || []).find((p) => p.id === pe.planId) || {}, pe.key)}
        </Typography>
      )}
      </Panel>

      {/* #943: in-app confirmation modal, replacing the native window.confirm the
          usage-reset action used. */}
      <ConfirmDialog
        open={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirm={() => { const c = confirm; setConfirm(null); if (c) c.onConfirm(); }}
        title={confirm?.title || ''}
        message={confirm?.message || ''}
        severity={confirm?.severity || null}
        confirmColor={confirm?.confirmColor || 'primary'}
        confirmLabel={confirm?.confirmLabel || 'Confirm'}
        testId={confirm?.testId || 'billing-confirm-dialog'}
      />
    </Box>
  );
}
